import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";

import { ArrowUpward, ArrowDownward, Edit, Delete } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../../components/Common/ConfirmationDialog";
import { useOutletContext } from "react-router-dom";
import BaseURL from "../../api/AppURL";


const AllRolePermission = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(25);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const [currentPage, setCurrentPage] = useState(1);

  // Confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [roleToDeleteId, setRoleToDeleteId] = useState(null);

  const { showSnackbar } = useOutletContext();
  const navigate = useNavigate();

  // Fetch roles + permissions
  const fetchRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${BaseURL}/all/roles/permission`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (response.data.status === "success") {
        setRoles(response.data.roles);
      } else {
        setError("Failed to load roles.");
      }
    } catch (err) {
      setError("Failed to load roles. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  // Search filtering
  const filteredRoles = useMemo(() => {
    if (!searchTerm) return roles;
    return roles.filter((role) =>
      role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.permissions.some((perm) => perm.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [roles, searchTerm]);

  // Sorting
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedRoles = useMemo(() => {
    const sortableItems = [...filteredRoles];
    if (!sortConfig.key) return sortableItems;

    sortableItems.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === "ascending" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });

    return sortableItems;
  }, [filteredRoles, sortConfig]);

  // Pagination calculations
  const indexOfLastRole = currentPage * entriesPerPage;
  const indexOfFirstRole = indexOfLastRole - entriesPerPage;
  const displayedRoles = sortedRoles.slice(indexOfFirstRole, indexOfLastRole);
  const totalPages = Math.ceil(sortedRoles.length / entriesPerPage);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "ascending" ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
  };

  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handlePreviousPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  const totalEntries = sortedRoles.length;
  const startEntry = totalEntries === 0 ? 0 : indexOfFirstRole + 1;
  const endEntry = Math.min(indexOfLastRole, totalEntries);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <Button
          key={i}
          variant={currentPage === i ? "contained" : "outlined"}
          color={currentPage === i ? "primary" : "inherit"}
          onClick={() => setCurrentPage(i)}
          sx={{ minWidth: 40, px: 1 }}
        >
          {i}
        </Button>
      );
    }
    return pageNumbers;
  };

  // Delete handlers
  const handleDeleteClick = (roleId) => {
    setRoleToDeleteId(roleId);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
    setRoleToDeleteId(null);
  };

  const handleConfirmDelete = async () => {
    if (!roleToDeleteId) return;
    setConfirmDialogOpen(false);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.delete(`${BaseURL}/delete/admin/role/${roleToDeleteId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (response.data.status === "success") {
        showSnackbar(response.data.message || "Role deleted successfully!", "success");
        await fetchRoles();
      } else {
        showSnackbar(response.data.message || "Failed to delete role.", "error");
      }
    } catch {
      showSnackbar("An error occurred while deleting the role.", "error");
    } finally {
      setRoleToDeleteId(null);
    }
  };

  const handleEditClick = (id) => {
    navigate(`/admin/edit-role-permission/${id}`);
  };

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Button variant="contained" onClick={() => navigate("/admin/add_role_permission")}>
              Add Role Permission
            </Button>
          </Box>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">Show</Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={entriesPerPage}
                    onChange={(e) => {
                      setEntriesPerPage(e.target.value);
                      setCurrentPage(1);
                    }}
                    displayEmpty
                    inputProps={{ "aria-label": "Entries per page" }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2">entries</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                label="Search Roles or Permissions"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                fullWidth
                sx={{ ml: { sm: "auto" } }}
              />
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Box sx={{ overflowX: "auto", minWidth: 650 }}>
                <Table aria-label="roles table">
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "5%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Sl
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "25%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Button
                          onClick={() => requestSort("name")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, whiteSpace: "nowrap" }}
                        >
                          Role Name {getSortIcon("name")}
                        </Button>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "50%",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Permission
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "20%", whiteSpace: "nowrap" }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedRoles.length > 0 ? (
                      displayedRoles.map((role, index) => (
                        <TableRow key={role.id}>
                          <TableCell sx={{verticalAlign: "top", borderRight: "1px solid #e0e0e0" }}>
                            {indexOfFirstRole + index + 1}
                          </TableCell>
                          <TableCell sx={{verticalAlign: "top", borderRight: "1px solid #e0e0e0" }}>{role.name}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                            {role.permissions.length > 0 ? (
                              role.permissions.map((perm) => (
                                <Chip
                                  key={perm.id}
                                  label={perm.name}
                                  size="small"
                                  sx={{
                                    // bgcolor: "#d32f2f", 
                                    bgcolor: "#f44336", 
                                    color: "white",
                                    mr: 0.5,
                                    mb: 0.5,
                                  }}
                                />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No permissions assigned
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{verticalAlign: "top"}}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button variant="outlined" size="small" onClick={() => handleEditClick(role.id)}>
                                <Edit fontSize="small" />
                              </Button>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleDeleteClick(role.id)}
                              >
                                <Delete fontSize="small" />
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                          No roles found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </TableContainer>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "center", sm: "center" },
              gap: 2,
              mt: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {startEntry} to {endEntry} of {totalEntries} entries
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={handlePreviousPage} disabled={currentPage === 1}>
                Prev
              </Button>
              {renderPageNumbers()}
              <Button variant="outlined" onClick={handleNextPage} disabled={currentPage === totalPages}>
                Next
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        onConfirm={handleConfirmDelete}
        title="Are you sure?"
        message="Delete This Role?"
        confirmText="Yes, delete it!"
        cancelText="Cancel"
      />
    </Box>
  );
};

export default AllRolePermission;
